import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { useCategories } from "@/hooks/useCategories";
import { ProductCard } from "@/components/ProductCard";
import { Filters } from "@/components/catalog/Filters";

const SORTS = [
  { v: "evidenza", l: "In evidenza" },
  { v: "novita", l: "Novità" },
  { v: "prezzo_asc", l: "Prezzo crescente" },
  { v: "prezzo_desc", l: "Prezzo decrescente" },
];

export default function Catalog() {
  const { slug } = useParams();
  const [sp, setSp] = useSearchParams();
  const cats = useCategories();
  const [facets, setFacets] = useState(null);
  const [products, setProducts] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const state = useMemo(() => ({
    q: sp.get("q") || "",
    sort: sp.get("sort") || "evidenza",
    color: sp.getAll("color"),
    material: sp.getAll("material"),
    available: sp.get("available") === "true",
    price: [Number(sp.get("min_price") ?? facets?.min_price ?? 0), Number(sp.get("max_price") ?? facets?.max_price ?? 1000)],
  }), [sp, facets]);

  useEffect(() => { api.get("/products/facets").then((r) => setFacets(r.data)); }, []);

  useEffect(() => {
    const params = new URLSearchParams(sp);
    if (slug) params.set("category", slug);
    setProducts(null);
    api.get(`/products?${params.toString()}`).then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, [sp, slug]);

  const update = (fn) => { const n = new URLSearchParams(sp); fn(n); setSp(n, { replace: true }); };
  const onToggle = (name, val) => update((n) => {
    const cur = n.getAll(name);
    n.delete(name);
    (cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val]).forEach((x) => n.append(name, x));
  });
  const onPrice = ([a, b]) => update((n) => { n.set("min_price", a); n.set("max_price", b); });
  const onAvailable = (v) => update((n) => (v ? n.set("available", "true") : n.delete("available")));
  const onReset = () => setSp(state.q ? { q: state.q } : {}, { replace: true });

  const cat = cats.find((c) => c.slug === slug);
  const title = state.q ? `Risultati per “${state.q}”` : cat?.name || "La collezione";
  const filterProps = { cats, activeCat: slug, facets, state, onToggle, onPrice, onAvailable, onReset };

  return (
    <div className="max-w-[1440px] mx-auto px-5 sm:px-8 pt-12" data-testid="catalog-page">
      <div className="mb-10">
        <div className="text-[11px] uppercase tracking-[0.25em] text-[#956842] mb-3">Boutique</div>
        <h1 data-testid="catalog-title" className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#39251D] leading-none">{title}</h1>
      </div>
      <div className="flex items-center justify-between gap-4 py-4 border-y border-[#D5B98C]/50 mb-10">
        <button data-testid="mobile-filters-button" onClick={() => setFiltersOpen(true)} className="lg:hidden inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em]">
          <SlidersHorizontal className="w-4 h-4" /> Filtri
        </button>
        <span data-testid="catalog-count" className="hidden lg:block text-xs uppercase tracking-[0.2em] text-[#242321]/60">{products ? `${products.length} prodotti` : "…"}</span>
        <Select value={state.sort} onValueChange={(v) => update((n) => n.set("sort", v))}>
          <SelectTrigger data-testid="catalog-sort-select" className="w-[200px] bg-transparent border-[#D5B98C] rounded-none text-sm"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#F5F0E8]">
            {SORTS.map((s) => <SelectItem key={s.v} value={s.v} data-testid={`sort-option-${s.v}`}>{s.l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid lg:grid-cols-[240px_1fr] gap-12">
        <aside className="hidden lg:block -mt-6"><Filters {...filterProps} /></aside>
        <div>
          {products && products.length === 0 && ([...sp.keys()].some((k) => k !== "sort") ? (
            <p data-testid="catalog-empty" className="py-20 text-center font-serif text-2xl text-[#39251D]/70">Nessun prodotto corrisponde ai filtri selezionati.</p>
          ) : (
            <div data-testid="catalog-coming-soon" className="py-20 text-center">
              <p className="font-serif text-3xl text-[#39251D]">{cat ? `${cat.name}: nuovi pezzi in arrivo` : "La collezione è in arrivo"}</p>
              <p className="mt-3 text-sm text-[#242321]/70">Stiamo fotografando i nostri prodotti. Per informazioni o per un pezzo in particolare, <Link to="/contatti" data-testid="catalog-coming-soon-contact" className="underline">contattaci</Link>.</p>
            </div>
          ))}
          <div className="grid grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-12 sm:gap-x-6" data-testid="catalog-grid">
            {(products || []).map((p) => <ProductCard key={p.slug} p={p} />)}
            {!products && Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[3/4] bg-[#E9DFCE] animate-pulse" />)}
          </div>
        </div>
      </div>
      <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
        <SheetContent side="left" className="bg-[#F5F0E8] w-[88vw] max-w-sm overflow-y-auto" data-testid="mobile-filters-sheet">
          <SheetTitle className="font-serif text-2xl text-[#39251D]">Filtri</SheetTitle>
          <Filters {...filterProps} />
          <button data-testid="mobile-filters-apply" onClick={() => setFiltersOpen(false)} className="w-full mt-8 bg-[#39251D] text-[#F5F0E8] py-3.5 text-xs uppercase tracking-[0.2em]">
            Mostra {products?.length ?? ""} {products?.length === 1 ? "prodotto" : "prodotti"}
          </button>
        </SheetContent>
      </Sheet>
    </div>
  );
}
